/**
 * The simulation profile: the values that pre-fill every mocked tool result in a run, the
 * customer among them. It rides the run payload as `simulation_profile`, which Brahma
 * overlays onto the mocked outputs.
 */

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { tabStorage } from '@/lib/tab-storage';
import type { CustomerDetails } from './customer-details';

/**
 * The simulated customer's identity (payload email and name, and the Customer card), as a
 * view onto the profile: who writes in is part of the world a run answers against, so it is
 * set up, kept and cleared with the rest of it.
 */
export const simulatorCustomerAtom = atom(
  (get): CustomerDetails => {
    const profile = get(simulationProfileAtom);
    return { email: profile.customer_email, name: profile.customer_name };
  },
  (get, set, next: CustomerDetails) => {
    set(simulationProfileAtom, { ...get(simulationProfileAtom), customer_email: next.email, customer_name: next.name });
  }
);

/**
 * Ids of the expanded sidebar sections. Sections start collapsed and the user
 * toggles them; loading a scenario case opens exactly the ones it filled.
 */
export const openSectionsAtom = atom<string[]>([]);

/** Sections a described world just filled; each leaves the list once its highlight has faded. */
export const highlightedSectionsAtom = atom<string[]>([]);

/** One line on the order. Configured as a list so a multi-item order is expressible. */
export interface OrderItem {
  title: string;
  quantity: string;
  price: string;
}

/** One format of the product. A list, because availability questions ask about one row. */
export interface ProductVariant {
  title: string;
  available_for_sale: boolean;
}

export interface SimulationProfileExtras {
  customer_email: string;
  customer_name: string;
  /** '' = the record exists (default); 'false' = the world has no such record
   *  and every tool that reads it returns its real not-found. */
  customer_exists: string;
  order_exists: string;
  subscription_exists: string;
  customer_phone: string;
  customer_address: string;
  order_number: string;
  order_amount: string;
  order_status: string;
  order_payment_status: string;
  order_date: string;
  order_delivery_date: string;
  order_tracking_number: string;
  order_carrier: string;
  order_tracking_url: string;
  /** Carrier or fulfilment state, e.g. 'In Transit' or 'Delivered'; this is what says a parcel arrived. */
  order_tracking_status: string;
  order_items: OrderItem[];
  subscription_id: string;
  subscription_status: string;
  /** Completed deliveries so far; some policies depend on it (e.g. cancel only after N). */
  subscription_delivery_count: string;
  subscription_product: string;
  subscription_frequency: string;
  subscription_next_date: string;
  subscription_price: string;
  product_title: string;
  /** 'true' | 'false' | '', derived from product_variants when those are set. */
  product_in_stock: string;
  product_price: string;
  product_variants: ProductVariant[];
}

export const EMPTY_SIMULATION_PROFILE: SimulationProfileExtras = {
  customer_email: '',
  customer_name: '',
  customer_exists: '',
  order_exists: '',
  subscription_exists: '',
  customer_phone: '',
  customer_address: '',
  order_number: '',
  order_amount: '',
  order_status: '',
  order_payment_status: '',
  order_date: '',
  order_delivery_date: '',
  order_tracking_number: '',
  order_carrier: '',
  order_tracking_url: '',
  order_tracking_status: '',
  order_items: [],
  subscription_id: '',
  subscription_status: '',
  subscription_delivery_count: '',
  subscription_product: '',
  subscription_frequency: '',
  subscription_next_date: '',
  subscription_price: '',
  product_title: '',
  product_in_stock: '',
  product_price: '',
  product_variants: [],
};

// Merge stored values over the empty profile so profiles saved before a field
// existed hydrate with every key present.
const jsonStorage = tabStorage<SimulationProfileExtras>();
const profileStorage = {
  getItem: (key: string, initialValue: SimulationProfileExtras): SimulationProfileExtras => ({
    ...EMPTY_SIMULATION_PROFILE,
    ...(jsonStorage.getItem(key, initialValue) ?? {}),
  }),
  setItem: jsonStorage.setItem,
  removeItem: jsonStorage.removeItem,
};

/**
 * The world one tab's run answers with; the tab next door is testing something else.
 * `getOnInit` so a reload has it before the first render, not one mount later with a
 * flash of empty fields.
 */
export const simulationProfileAtom = atomWithStorage<SimulationProfileExtras>(
  'sim-profile-kim-ai',
  EMPTY_SIMULATION_PROFILE,
  profileStorage,
  { getOnInit: true }
);

/**
 * True runs Mocked (tools answer from the profile world); false runs Live data (reads hit
 * live systems, writes are still simulated). Not kept: a reload starts mocked, so a mode
 * picked in another tab, or hours ago, never decides whether this run reads live systems.
 */
export const simulateToolsAtom = atom<boolean>(true);

/** The fixed-option fields, and the only values their controls can display. */
export const ORDER_STATUS_OPTIONS = ['fulfilled', 'partially_fulfilled', 'unfulfilled', 'in_progress', 'on_hold', 'scheduled'];
export const PAYMENT_STATUS_OPTIONS = ['paid', 'partially_paid', 'pending', 'authorized', 'partially_refunded', 'refunded', 'voided'];
export const SUBSCRIPTION_STATUS_OPTIONS = ['active', 'paused', 'cancelled'];
/** In the subscription's own unit: every 4 weeks and monthly are different records. */
export const SUBSCRIPTION_FREQUENCY_OPTIONS = [
  'weekly',
  'every_2_weeks',
  'every_3_weeks',
  'every_4_weeks',
  'every_5_weeks',
  'every_6_weeks',
  'every_8_weeks',
  'every_10_weeks',
  'every_12_weeks',
  'every_13_weeks',
  'every_14_weeks',
  'every_16_weeks',
  'monthly',
  'every_2_months',
  'quarterly',
  'every_4_months',
  'every_6_months',
  'yearly'
];

/**
 * Whether a configuration section holds anything, by the prefix its fields share. A record
 * declared absent (`*_exists: 'false'`) counts as configured; a list counts only once a row
 * carries something, matching what `buildSimulationProfile` sends.
 */
export const sectionConfigured = (
  profile: SimulationProfileExtras,
  prefix: 'customer' | 'order' | 'subscription' | 'product'
): boolean =>
  Object.entries(profile).some(([key, value]) => {
    if (!key.startsWith(`${prefix}_`)) return false;
    if (Array.isArray(value)) {
      return (value as Array<OrderItem | ProductVariant>).some((row) =>
        Object.values(row).some((field) => typeof field === 'string' && field.trim() !== '')
      );
    }
    return String(value ?? '').trim() !== '';
  });

/** A profile value as it rides the run payload. */
type SimulationProfileValue = string | boolean | OrderItem[] | ProductVariant[];

/**
 * The non-empty entries to send with a run, or undefined when nothing is set. Values are
 * trimmed strings, except lists and booleans, which ride as they are; an empty list is
 * dropped like an empty string.
 */
export const buildSimulationProfile = (
  extras: SimulationProfileExtras
): Record<string, SimulationProfileValue> | undefined => {
  const variants = extras.product_variants.filter((v) => v.title.trim());
  // Boolean, not 'false': it lands on boolean fields, where the string read as truthy.
  const inStock: SimulationProfileValue = variants.length
    ? variants.some((v) => v.available_for_sale)
    : extras.product_in_stock === ''
      ? ''
      : extras.product_in_stock === 'true';
  const merged: Record<string, SimulationProfileValue> = {
    ...extras,
    product_variants: variants,
    product_in_stock: inStock,
  };
  const compact = Object.fromEntries(
    Object.entries(merged)
      .map(([k, v]): [string, SimulationProfileValue] =>
        Array.isArray(v) || typeof v === 'boolean' ? [k, v] : [k, String(v ?? '').trim()]
      )
      .filter(([, v]) => (Array.isArray(v) ? v.length > 0 : v !== ''))
  );
  return Object.keys(compact).length ? compact : undefined;
};
